/**
 * The twelve lanes — the reasons a piece exists.
 *
 * One lane per piece. A lane is not a "content type"; it is the ARGUMENT the
 * piece makes. Every lane carries a hard requirement, and a lane whose
 * requirement is unmet is REFUSED rather than written thin — that is the whole
 * mechanism, and it is the reason `comparison` disappears on an account with
 * no named competitors instead of inventing one.
 *
 * Two things are deliberately absent. There is no shame lane and no
 * "the real reason X got worse" conspiracy lane: legal on a paid network,
 * corrosive on an organic feed, and it damages the account that posted it.
 * A meme / trend-jack register exists but is offered, never defaulted.
 */

import type { OfferClassId } from './offer-class';

export type LaneId =
  | 'proof_in_public'
  | 'the_mistake'
  | 'mechanism'
  | 'objection_head_on'
  | 'behind_the_work'
  | 'customer_voice'
  | 'comparison'
  | 'teaching_unit'
  | 'point_of_view'
  | 'occasion'
  | 'origin_stake'
  | 'utility_asset';

/** What a lane needs before it may be used at all. */
export type LaneRequirement =
  | 'metric'
  | 'audience_language'
  | 'named_mechanism'
  | 'real_objection'
  | 'process_detail'
  | 'customer_quote'
  | 'named_alternative'
  | 'usable_takeaway'
  | 'falsifiable_claim'
  | 'dated_trigger'
  | 'founder_decision'
  | 'deliverable_asset';

export interface Lane {
  id: LaneId;
  label: string;
  argument: string;
  eligible: OfferClassId[];
  requires: LaneRequirement;
  /** The sentence shown when the lane is refused for want of its requirement. */
  refusal: string;
  /** Lanes that carry proof, for the slate balance rule. */
  bearsProof: boolean;
  /** Lanes that carry an opinion or a lesson, for the slate balance rule. */
  bearsOpinion: boolean;
  /** Hard cap on how many of these may appear in one slate, if any. */
  maxPerSlate?: number;
}

const ALL: OfferClassId[] = ['commerce', 'service', 'authority', 'unclear'];
const EVIDENCE_ONLY: OfferClassId[] = ['commerce', 'service', 'authority', 'unclear'];

export const LANES: Record<LaneId, Lane> = {
  proof_in_public: {
    id: 'proof_in_public',
    label: 'Proof in public',
    argument: 'A real result, with a real number.',
    eligible: EVIDENCE_ONLY,
    requires: 'metric',
    refusal: 'No metric on file that this account actually produced.',
    bearsProof: true,
    bearsOpinion: false,
  },
  the_mistake: {
    id: 'the_mistake',
    label: 'The mistake',
    argument: 'The specific wrong thing this audience keeps doing.',
    eligible: ['commerce', 'service', 'authority'],
    requires: 'audience_language',
    refusal: 'No audience language on file to state the mistake in their words.',
    bearsProof: false,
    bearsOpinion: true,
  },
  mechanism: {
    id: 'mechanism',
    label: 'Mechanism',
    argument: 'How it actually works, one level deeper than the reader expected.',
    eligible: ALL,
    requires: 'named_mechanism',
    refusal: 'The source names no mechanism — only benefits, which is not the same thing.',
    bearsProof: false,
    bearsOpinion: false,
  },
  objection_head_on: {
    id: 'objection_head_on',
    label: 'Objection, head on',
    argument: 'The reason not to buy, answered.',
    eligible: ['commerce', 'service', 'authority'],
    requires: 'real_objection',
    refusal: 'No real objection on file — an invented one is worse than none.',
    bearsProof: false,
    bearsOpinion: true,
  },
  behind_the_work: {
    id: 'behind_the_work',
    label: 'Behind the work',
    argument: 'Process, craft, the unglamorous middle.',
    eligible: ['commerce', 'service'],
    requires: 'process_detail',
    refusal: 'The source describes no part of how the work is actually done.',
    bearsProof: false,
    bearsOpinion: false,
  },
  customer_voice: {
    id: 'customer_voice',
    label: 'Customer voice',
    argument: 'A real quote as the spine of the piece.',
    eligible: EVIDENCE_ONLY,
    requires: 'customer_quote',
    refusal: 'No review, testimonial or message on file to quote.',
    bearsProof: true,
    bearsOpinion: false,
  },
  comparison: {
    id: 'comparison',
    label: 'Comparison',
    argument: 'This against the default alternative, including doing nothing.',
    eligible: ['commerce', 'service', 'authority'],
    requires: 'named_alternative',
    refusal: 'No competitor or status-quo alternative named — you cannot compare to nobody.',
    bearsProof: true,
    bearsOpinion: false,
  },
  teaching_unit: {
    id: 'teaching_unit',
    label: 'Teaching unit',
    argument: 'One complete, usable thing the reader can do today.',
    eligible: ['service', 'authority'],
    requires: 'usable_takeaway',
    refusal: 'Nothing in the source is usable without buying first.',
    bearsProof: false,
    bearsOpinion: true,
  },
  point_of_view: {
    id: 'point_of_view',
    label: 'Point of view',
    argument: 'A claim the reader could disagree with.',
    eligible: ['authority', 'service'],
    requires: 'falsifiable_claim',
    refusal: 'The source states nothing anyone could argue with.',
    bearsProof: false,
    bearsOpinion: true,
  },
  occasion: {
    id: 'occasion',
    label: 'Occasion',
    argument: 'The moment that makes this relevant right now.',
    eligible: ['commerce', 'service'],
    requires: 'dated_trigger',
    refusal: 'No dated or triggered reason to post this now.',
    bearsProof: false,
    bearsOpinion: false,
  },
  origin_stake: {
    id: 'origin_stake',
    label: 'Origin and stake',
    argument: 'Why this exists, told through one decision.',
    eligible: ALL,
    requires: 'founder_decision',
    refusal: 'No founding decision on file to tell this through.',
    bearsProof: false,
    bearsOpinion: true,
    maxPerSlate: 1,
  },
  utility_asset: {
    id: 'utility_asset',
    label: 'Utility asset',
    argument: 'A checklist, template or calculator the reader keeps.',
    eligible: ALL,
    requires: 'deliverable_asset',
    refusal: 'The asset does not exist yet, so the piece would promise something undeliverable.',
    bearsProof: false,
    bearsOpinion: true,
  },
};

export const LANE_IDS = Object.keys(LANES) as LaneId[];

/**
 * The evidence-only subset. Used when the offer class is `unclear`: the
 * identity and point-of-view lanes are withheld because they require knowing
 * what the business IS, which by definition we do not.
 */
export const EVIDENCE_ONLY_LANES: LaneId[] = [
  'proof_in_public',
  'mechanism',
  'behind_the_work',
  'customer_voice',
];

export interface LaneAvailability {
  metric: boolean;
  audienceLanguage: boolean;
  namedMechanism: boolean;
  realObjection: boolean;
  processDetail: boolean;
  customerQuote: boolean;
  namedAlternative: boolean;
  usableTakeaway: boolean;
  falsifiableClaim: boolean;
  datedTrigger: boolean;
  founderDecision: boolean;
  deliverableAsset: boolean;
}

const REQUIREMENT_KEY: Record<LaneRequirement, keyof LaneAvailability> = {
  metric: 'metric',
  audience_language: 'audienceLanguage',
  named_mechanism: 'namedMechanism',
  real_objection: 'realObjection',
  process_detail: 'processDetail',
  customer_quote: 'customerQuote',
  named_alternative: 'namedAlternative',
  usable_takeaway: 'usableTakeaway',
  falsifiable_claim: 'falsifiableClaim',
  dated_trigger: 'datedTrigger',
  founder_decision: 'founderDecision',
  deliverable_asset: 'deliverableAsset',
};

export interface LaneEligibility {
  allowed: LaneId[];
  refused: { lane: LaneId; reason: string }[];
}

/**
 * Which lanes this run may actually use. Both filters are hard:
 * the class filter (a `commerce` account has no `teaching_unit` lane) and the
 * evidence filter (no metric on file ⇒ no `proof_in_public`).
 */
export function eligibleLanes(
  offerClass: OfferClassId,
  available: LaneAvailability,
): LaneEligibility {
  const allowed: LaneId[] = [];
  const refused: { lane: LaneId; reason: string }[] = [];

  const pool = offerClass === 'unclear' ? EVIDENCE_ONLY_LANES : LANE_IDS;

  for (const id of LANE_IDS) {
    const lane = LANES[id];
    if (!pool.includes(id)) {
      refused.push({
        lane: id,
        reason: 'Held back — we could not tell what this business sells.',
      });
      continue;
    }
    if (!lane.eligible.includes(offerClass)) {
      refused.push({
        lane: id,
        reason: `Not a ${offerClass} lane.`,
      });
      continue;
    }
    if (!available[REQUIREMENT_KEY[lane.requires]]) {
      refused.push({ lane: id, reason: lane.refusal });
      continue;
    }
    allowed.push(id);
  }

  return { allowed, refused };
}
